import { Component, Input } from '@angular/core';

import { Airport, AirportOwnershipType, AirportStatus, AirportType } from '../../airport/models/airport.model';
import { LandingSurfaceType } from '../../airport/models/landing-surface.model';
import { AirportSearchResult } from '../../airport/models/airport-search-result.model';


@Component({
  selector: 'app-airport-icons',
  templateUrl: './airport-icons.component.html',
  styleUrls: ['./airport-icons.component.scss']
})
export class AirportIconsComponent {
  @Input() airport: Airport|AirportSearchResult;
  @Input() compact = false;

  /**
   * Returns true if the closed badge should be displayed.
   */
  get closed(): boolean {
    return this.airport.status === AirportStatus.ClosedIndefinitely
      || this.airport.status === AirportStatus.ClosedPermanently;
  }

  /**
   * Returns the label that should be displayed on the closed badge.
   */
  get closedLabel(): string {
    return Airport.formatAirportStatus(this.airport.status);
  }

  /**
   * Returns true if the civil badge should be displayed.
   */
  get civil(): boolean {
    return this.airport.airportOwnership === AirportOwnershipType.Civil;
  }

  /**
   * Returns true if the privately-owned badge should be displayed.
   */
  get privateOwn(): boolean {
    return this.airport.airportOwnership === AirportOwnershipType.Private;
  }

  /**
   * Returns true if the military badge should be displayed.
   */
  get military(): boolean {
    return this.airport.militaryJoint
      || this.airport.airportOwnership === AirportOwnershipType.Army
      || this.airport.airportOwnership === AirportOwnershipType.Navy
      || this.airport.airportOwnership === AirportOwnershipType.USAF
      || this.airport.airportOwnership === AirportOwnershipType.CoastGuard;
  }

  /**
   * Returns the label to use on the military badge.
   * @returns {string}
   */
  get militaryLabel(): string {
    let type = '';
    if (this.airport.airportOwnership === AirportOwnershipType.Army
      || this.airport.airportOwnership === AirportOwnershipType.Navy
      || this.airport.airportOwnership === AirportOwnershipType.USAF
      || this.airport.airportOwnership === AirportOwnershipType.CoastGuard) {
      type = Airport.formatAirportOwnershipType(this.airport.airportOwnership);
      if (this.airport.militaryJoint) {
        type = 'Civilian/' + type;
      }
    }
    let label = this.airport.militaryJoint ? 'Joint Use' : 'Military';
    if (type) {
      label += ' (' + type + ')';
    }
    return label;
  }

  /**
   * Returns true if the private use badge should be shown.
   */
  get privateUse(): boolean {
    if (this.military) {
      return false;
    }
    return this.airport.publicUse !== null && !this.airport.publicUse;
  }

  /**
   * Returns true if the public use badge should be shown.
   */
  get publicUse(): boolean {
    return this.airport.publicUse;
  }

  /**
   * Returns true if the unpaved badge should be shown.
   */
  get unpaved(): boolean {
    if (this.airport instanceof AirportSearchResult) {
      return !this.airport.paved;
    }
    if (this.airport instanceof Airport) {
      return (this.airport.hasLandingSurfaceType(LandingSurfaceType.Runway)
        || this.airport.hasLandingSurfaceType(LandingSurfaceType.Helipad))
        && this.airport.landingSurfaces.every(sfc => !sfc.paved);
    }
    return false;
  }

  /**
   * Returns true if the heliport badge should be shown.
   */
  get heliport(): boolean {
    return this.airport.type === AirportType.Heliport;
  }

  /**
   * Returns true if the seaplane base badge should be shown.
   */
  get seaport(): boolean {
    return this.airport.type === AirportType.SeaplaneBase;
  }

  /**
   * Returns true if the ultralight badge should be shown.
   */
  get ultralight(): boolean {
    return this.airport.type === AirportType.Ultralight;
  }

  /**
   * Returns true if the gliderport badge should be shown.
   */
  get gliderport(): boolean {
    return this.airport.type === AirportType.GliderPort;
  }

  /**
   * Returns true if the balloonport badge should be shown.
   */
  get balloonport(): boolean {
    return this.airport.type === AirportType.BalloonPort;
  }

  /**
   * Returns true if the airport of entry badge should be shown.
   */
  get aoe(): boolean {
    return this.airport.customsIntlEntry;
  }
}
